import { FilterableMultiSelect } from '@carbon/react';
import type { FacetBucket } from '../types';
import { bucketLabel } from './facet-label';

interface Item {
  id: string;
  label: string;
}

interface FacetMultiSelectProps {
  /** Field id, used to namespace the control (`filter-<id>`). */
  id: string;
  /** The field's label, which also names the control. */
  titleText: string;
  /** Keep the label for assistive tech only, where the caller shows it itself. */
  hideLabel?: boolean;
  placeholder: string;
  facetBuckets: FacetBucket[];
  selectedIds: string[];
  /** Noun appended after each option's count (e.g. "Documents"). */
  countNoun?: string;
  /** Option counts beside each option (default true). */
  showCounts?: boolean;
  /** Small, with the chosen options pinned on top and a menu that floats from
   *  the field's left edge, clear of a scrolling container. */
  compact?: boolean;
  /** Searches the options on the server as the reader types; the buckets passed
   *  in are then the matches, listed as they come. */
  onSearchChange?: (text: string) => void;
  onChange: (ids: string[]) => void;
}

/**
 * Carbon types `direction` as 'top' | 'bottom' but hands it to floating-ui as
 * the menu's placement, which also takes a start or end alignment.
 * @param {string} placement The floating-ui placement.
 * @return {object} The `direction` prop that places the menu there.
 */
function menuPlacement(placement: 'bottom-start' | 'bottom-end') {
  return { direction: placement as 'bottom' };
}

/**
 * Lists the options as the server returned them, unfiltered by the typed text.
 * @param {Item[]} items The options.
 * @return {Item[]} The same options.
 */
const serverFiltered = (items: Item[]) => items;

/**
 * Controlled searchable multi-select over facet buckets. The tags variant wraps
 * this and overlays its own pills; both stay in sync via `selectedIds`.
 * @param {FacetMultiSelectProps} props The multi-select.
 * @return {JSX.Element} The multi-select.
 */
export function FacetMultiSelect({
  id,
  titleText,
  hideLabel = false,
  placeholder,
  facetBuckets,
  selectedIds,
  countNoun,
  showCounts = true,
  compact = false,
  onSearchChange,
  onChange,
}: FacetMultiSelectProps) {
  const items: Item[] = facetBuckets.map((bucket) => ({
    id: bucket.id,
    label: bucketLabel(bucket, countNoun, { showCount: showCounts }),
  }));
  const selectedItems = items.filter((i) => selectedIds.includes(i.id));

  return (
    <FilterableMultiSelect
      id={`filter-${id}`}
      titleText={titleText}
      hideLabel={hideLabel}
      placeholder={placeholder}
      items={items}
      itemToString={(item: Item) => (item ? item.label : '')}
      selectedItems={selectedItems}
      onChange={({ selectedItems: next }: { selectedItems: Item[] }) =>
        onChange(next.map((i) => i.id))
      }
      selectionFeedback={compact ? 'top' : 'fixed'}
      {...(onSearchChange && {
        filterItems: serverFiltered,
        // Typed as Downshift's change object, but Carbon passes the text.
        onInputValueChange: (text: unknown) =>
          onSearchChange(typeof text === 'string' ? text : ''),
      })}
      {...(compact && {
        size: 'sm' as const,
        autoAlign: true,
        ...menuPlacement('bottom-start'),
      })}
    />
  );
}
